import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { loggedInState, userInfoState } from '../recoil/atoms';

// Pages
import Login from '../pages/Login';
import ChangePassword from '../pages/ChangePassword';
import LandingPage from '../pages/LandingPage';
import RegisterPage from '../pages/RegisterPage';
import AppPreviewPage from '../pages/AppPreviewPage';

// Admin Pages
import AdminLayout from '../components/AdminLayout';
import AdminDashboard from '../pages/admin/Dashboard';
import ShopsList from '../pages/admin/ShopsList';
import Subscribers from '../pages/admin/Subscribers';
import Payments from '../pages/admin/Payments';
import Requests from '../pages/admin/Requests';

// Shop Pages
import ShopLayout from '../components/ShopLayout';
import ShopDashboard from '../pages/shop/Dashboard';
import NewInvoice from '../pages/shop/NewInvoice';
import InvoicesList from '../pages/shop/InvoicesList';
import Customers from '../pages/shop/Customers';
import Profile from '../pages/shop/Profile';
import SalesStats from '../pages/shop/SalesStats';
import NewOrder from '../pages/shop/NewOrder';
import OrdersList from '../pages/shop/OrdersList';
import OrderDetail from '../pages/shop/OrderDetail';
import AddMeasurement from '../pages/shop/AddMeasurement';
import CreateInvoice from '../pages/shop/CreateInvoice';
import Tailors from '../pages/shop/Tailors';
import Installers from '../pages/shop/Installers';
import Salespersons from '../pages/shop/Salespersons';

// Protected Route - فقط چک لاگین
const ProtectedRoute = ({ children, allowedRoles }) => {
  const isLoggedIn = useRecoilValue(loggedInState);
  const userInfo = useRecoilValue(userInfoState);

  if (!isLoggedIn || !userInfo) {
    return <Navigate to="/login" replace />;
  }

  const userRole = userInfo.role?.toLowerCase() || '';
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    // Admin → /admin, Shop & Salesperson → /shop
    const redirectTo = userRole === 'admin' ? '/admin' : '/shop';
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

// صفحه تغییر رمز - فقط برای لاگین اولیه
const ChangePasswordRoute = () => {
  const isLoggedIn = useRecoilValue(loggedInState);
  const userInfo = useRecoilValue(userInfoState);

  if (!isLoggedIn || !userInfo) {
    return <Navigate to="/login" replace />;
  }

  if (!userInfo.first_time_login) {
    const userRole = userInfo.role?.toLowerCase() || '';
    const redirectTo = userRole === 'admin' ? '/admin' : '/shop';
    return <Navigate to={redirectTo} replace />;
  }

  return <ChangePassword />;
};

// Home - اگر لاگین بود به پنل، در غیر این صورت صفحه اصلی عمومی
const HomeRedirect = () => {
  const isLoggedIn = useRecoilValue(loggedInState);
  const userInfo = useRecoilValue(userInfoState);

  if (!isLoggedIn || !userInfo) {
    return <LandingPage />;
  }

  if (userInfo.first_time_login) {
    return <Navigate to="/change-password" replace />;
  }

  const userRole = userInfo.role?.toLowerCase() || '';
  if (userRole === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/shop" replace />;
};

// Login Route
const LoginRoute = () => {
  const isLoggedIn = useRecoilValue(loggedInState);
  const userInfo = useRecoilValue(userInfoState);

  if (isLoggedIn && userInfo) {
    if (userInfo.first_time_login) {
      return <Navigate to="/change-password" replace />;
    }
    const userRole = userInfo.role?.toLowerCase() || '';
    if (userRole === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/shop" replace />;
  }

  return <Login />;
};

// Main Router
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginRoute />} />

      {/* صفحه مستقل ثبت‌نام و مشاوره (عمومی - برای سئو و URL مخصوص) */}
      <Route path="/register" element={<RegisterPage />} />

      {/* صفحه مستقل نمای برنامه (عمومی - برای سئو) */}
      <Route path="/preview" element={<AppPreviewPage />} />

      {/* تغییر رمز عبور - مسیر جداگانه */}
      <Route path="/change-password" element={<ChangePasswordRoute />} />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="shops" element={<ShopsList />} />
        <Route path="subscribers" element={<Subscribers />} />
        <Route path="payments" element={<Payments />} />
        <Route path="requests" element={<Requests />} />
      </Route>

      {/* Shop Routes - صاحب فروشگاه و فروشنده */}
      <Route path="/shop" element={
        <ProtectedRoute allowedRoles={['shop', 'salesperson']}>
          <ShopLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ShopDashboard />} />
        <Route path="invoices/new" element={<NewInvoice />} />
        <Route path="invoices" element={<InvoicesList />} />
        <Route path="customers" element={<Customers />} />
        <Route path="stats" element={<SalesStats />} />
        <Route path="profile" element={<Profile />} />
        <Route path="orders/new" element={<NewOrder />} />
        <Route path="orders" element={<OrdersList />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="orders/:id/measurement" element={<AddMeasurement />} />
        <Route path="orders/:id/invoice" element={<CreateInvoice />} />
        <Route path="tailors" element={<Tailors />} />
        <Route path="installers" element={<Installers />} />
        <Route path="salespersons" element={<Salespersons />} />
      </Route>

      {/* Default */}
      <Route path="/" element={<HomeRedirect />} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
};

export default AppRoutes;
